import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Detects keyboard height on iOS AND Android Capacitor builds.
 *
 * iOS fires `keyboardWillShow` / `keyboardWillHide` (with animation context).
 * Android only fires `keyboardDidShow` / `keyboardDidHide` (no will-prefixed
 * events). The previous hook only listened to `keyboardWillShow` so the
 * keyboard height stayed at 0 on Android, which caused inputs to be hidden
 * behind the system keyboard. We now listen to BOTH event families.
 */
export function useKeyboardHeight() {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const listeners = [];
      import('@capacitor/keyboard').then(({ Keyboard }) => {
        const onShow = (info) => setKeyboardHeight(info?.keyboardHeight || 0);
        const onHide = () => setKeyboardHeight(0);
        listeners.push(Keyboard.addListener('keyboardWillShow', onShow));
        listeners.push(Keyboard.addListener('keyboardDidShow', onShow));
        listeners.push(Keyboard.addListener('keyboardWillHide', onHide));
        listeners.push(Keyboard.addListener('keyboardDidHide', onHide));
      }).catch(() => {});

      return () => {
        listeners.forEach((p) => p?.then?.((l) => l.remove?.()));
      };
    }

    const handleResize = () => {
      if (window.visualViewport) {
        const keyboardH = window.innerHeight - window.visualViewport.height;
        setKeyboardHeight(keyboardH > 100 ? keyboardH : 0);
      }
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    }
    return undefined;
  }, []);

  return keyboardHeight;
}
